import { gql } from '@apollo/client'
import { Heading, Link, Snippet } from '@farol-ds/react'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { COMPONENT_CLASS, GA4ClickEvent } from '@jusbrasil-web/shared-ga4-events'
import classNames from 'classnames'
import NextLink from 'next/link'
import { useEffect } from 'react'

import { useMetrics } from '../../hooks'
import printStyles from '../../print.module.scss'
import { mapToGA4ComponentName } from '../ga4-utils'
import { RecommendedDocuments_DocumentFragment } from './__generated__/recommended-documents.graphql'
import { getEventProps, getRecommendedDocuments } from './functions'
import styles from './recommended-documents.module.scss'

export interface RecommendedDocumentsProps {
  document: RecommendedDocuments_DocumentFragment
}

export function RecommendedDocuments(props: RecommendedDocumentsProps) {
  const { document } = props
  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()
  const { sendGA4Event } = useGA4Dispatcher()
  const recommendedDocuments = getRecommendedDocuments(document)

  useEffect(() => {
    if (recommendedDocuments.length === 0) return

    let eventTriggered = false

    const observer = new IntersectionObserver(
      (entries) => {
        if (!eventTriggered && entries[0]?.isIntersecting) {
          eventTriggered = true

          sendMetrics('Recommendation.Viewed', getEventProps(recommendedDocuments))

          observer.disconnect()
        }
      },
      { threshold: 0.25 }
    )
    observer.observe(dispatcherRef.current as HTMLDivElement)

    return () => {
      observer.disconnect()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.recommendations])

  if (recommendedDocuments.length === 0) {
    return null
  }

  const handleClick = (index: number) => {
    const doc = recommendedDocuments[index]
    if (!doc) return
    sendMetrics('Recommendation.Click', getEventProps(recommendedDocuments, index))
    sendGA4Event(
      new GA4ClickEvent({
        componentClass: COMPONENT_CLASS.RELATED_DOCUMENTS,
        selectedOption: `position_${index}`,
        componentName: mapToGA4ComponentName(COMPONENT_CLASS.RELATED_DOCUMENTS, doc.artifact),
      })
    )
  }

  return (
    <article className={classNames(styles.wrapper, printStyles.unprintable)} ref={dispatcherRef}>
      <Heading size="xl" asChild>
        <h2>Informações relacionadas</h2>
      </Heading>

      <div className={styles.related}>
        {recommendedDocuments.map((doc, index) => (
          <Snippet key={doc.docId} data-text-from-component={`${doc.artifact}:${doc.docId}`}>
            <Snippet.Container>
              <Snippet.Header>
                <Snippet.Title asChild>
                  <h3 className={styles.header}>
                    <Link asChild>
                      <NextLink href={doc.url} onClick={() => handleClick(index)}>
                        {doc.title}
                      </NextLink>
                    </Link>
                  </h3>
                </Snippet.Title>

                <Snippet.Caption>
                  <Snippet.CaptionItem>{doc.artifactLabel}</Snippet.CaptionItem>
                  {!!doc.typeLabel && <Snippet.CaptionItem>{doc.typeLabel}</Snippet.CaptionItem>}
                  {!!doc.formattedDate && (
                    <Snippet.CaptionItem>
                      Data de publicação: {doc.formattedDate}
                    </Snippet.CaptionItem>
                  )}
                  {!!doc.publisherName && (
                    <Snippet.CaptionItem>{doc.publisherName}</Snippet.CaptionItem>
                  )}
                </Snippet.Caption>
              </Snippet.Header>

              <Snippet.Body>
                <Snippet.Content quote={doc.hasQuote} className={styles.snippetContent}>
                  {doc.description}
                </Snippet.Content>
              </Snippet.Body>
            </Snippet.Container>
          </Snippet>
        ))}
      </div>
    </article>
  )
}

RecommendedDocuments.fragments = {
  document: gql`
    fragment RecommendedDocuments_document on Document {
      recommendations(recommenderId: "default", limit: 5, personaRerank: true) @ignoreExceptions {
        document {
          docId
          artifact
          type
          title
          date
          url
          description(maxLength: 700, stripTags: true)
          summaryPrecedentStatement(stripTags: true)
          jurisTypeLabel
          motionTypeLabel
          publisher {
            fullname
          }
        }
        score
        scoreDetails {
          algorithmName
          algorithmScore
          algorithmWeight
        }
      }
    }
  `,
}
